/**
 * CelebrationOverlay — Enhanced kid-friendly celebration
 * Confetti + Fireworks + MindoraCharacter + ScreenShake + sound
 */

import { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { springs } from '../animation';
import { useSoundEffects } from '../hooks';
import MindoraCharacter from './character/MindoraCharacter';
import Confetti from './effects/Confetti';
import Fireworks from './effects/Fireworks';
import ScreenShake from './effects/ScreenShake';
import { SparkleCluster } from './decorations';

interface CelebrationOverlayProps {
  show: boolean;
  onClose: () => void;
  message?: string;
  bonusText?: string;
}

export default function CelebrationOverlay({
  show,
  onClose,
  message = 'Amazing Job!',
  bonusText = '+50 Bonus Points!',
}: CelebrationOverlayProps) {
  const { play } = useSoundEffects();

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (show) {
      play('celebratoryWoohoo');
      const timer = setTimeout(onClose, 4500);
      return () => clearTimeout(timer);
    }
  }, [show, onClose, play]);

  return (
    <>
      {/* Effects layer (always rendered, controlled by trigger) */}
      <Confetti trigger={show} count={50} />
      <Fireworks trigger={show} bursts={4} />

      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="celebration-title"
            onKeyDown={handleKeyDown}
            tabIndex={-1}
          >
            {/* Center message with ScreenShake */}
            <ScreenShake trigger={show}>
              <motion.button
                initial={{ scale: 0, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={springs.bouncy}
                className="bg-kid-surface rounded-kid shadow-kid-lg p-8 text-center max-w-sm mx-4 border-2 border-kid-border relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-kid-ocean"
                onClick={onClose}
                aria-label="Close celebration"
              >
                <SparkleCluster count={8} spread={40} className="absolute -top-6 left-1/2 -translate-x-1/2" />

                {/* Mascot */}
                <div className="flex justify-center mb-3">
                  <MindoraCharacter mood="celebrating" size="lg" />
                </div>

                {/* Celebration emoji */}
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ repeat: 3, duration: 0.5 }}
                  className="text-5xl mb-3"
                  aria-hidden="true"
                >
                  🎉
                </motion.div>

                {/* Message */}
                <h2 id="celebration-title" className="font-display text-kid-2xl text-kid-text mb-2" role="alert">
                  {message}
                </h2>
                <p className="text-kid-text-soft font-kid text-kid-sm mb-1">
                  You completed all your habits today!
                </p>
                <p className="font-display text-kid-lg text-kid-dandelion">
                  {bonusText}
                </p>
              </motion.button>
            </ScreenShake>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
